import simpleRestProvider from "ra-data-simple-rest";
import { fetchUtils } from "react-admin";

type MenuStatType = "loan" | "application" | "customer" | "ach" | "quickLink";

const createCustomFetchJson = (apiUrl: string) => {
  return (url: string, options: fetchUtils.Options = {}) => {
    const fullUrl = url.startsWith("http") ? url : `${apiUrl}/${url}`;
    const headers = new Headers(
      options.headers || { Accept: "application/json" }
    );

    const token = localStorage.getItem("token");
    const accessToken = localStorage.getItem("access_token");
    const csrfToken = localStorage.getItem("csrfToken");

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (csrfToken) {
      headers.set("X-CSRF-TOKEN", csrfToken);
      headers.set("X-XSRF-TOKEN", csrfToken);
    }

    headers.set("reqDivision", localStorage.getItem("division") || "");
    headers.set("reqBank", localStorage.getItem("bank") || "");
    headers.set("reqAdmin", localStorage.getItem("permissions") || "");
    headers.set("reqType", "");

    return fetchUtils.fetchJson(fullUrl, {
      ...options,
      headers,
      user: {
        authenticated: !!token,
        token: token || "",
      },
    });
  };
};

const API_BASE_URL = import.meta.env.VITE_API_URL;
const customFetchJson = createCustomFetchJson(API_BASE_URL);
const dataProvider = simpleRestProvider(API_BASE_URL, customFetchJson);

const getStatEndpoint = (type: MenuStatType): string => {
  switch (type) {
    case "loan":
      return "total/loan";
    case "application":
      return "total/application";
    case "customer":
      return "total/customer";
    case "ach":
      return "total/ach";
    case "quickLink":
      return "total/quicklinkBox";
    default:
      return "total/loan";
  }
};

/**
 * save menu stats to localStorage
 */
const getStorageKey = (type: MenuStatType): string => {
  return `${type}MenuStats`;
};

/**
 * preload menu stats and save to localStorage
 * @param type datatype: loan | application | customer | ach
 * @returns Promise<void>
 */
const preloadMenuStats = async (type: MenuStatType = "loan"): Promise<void> => {
  try {
    const endpoint = getStatEndpoint(type);
    const response = await dataProvider.getOne(endpoint, { id: 1 });
    const storageKey = getStorageKey(type);

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        data: response.data.total || 0,
        lastUpdated: Date.now(),
      })
    );
  } catch (error) {
    console.error(`Failed to preload ${type} menu stats:`, error);
    throw error;
  }
};

const preloadAllMenuStats = async (): Promise<void> => {
  const types: MenuStatType[] = [
    "loan",
    "application",
    "customer",
    "ach",
    "quickLink",
  ];
  await Promise.all(types.map((type) => preloadMenuStats(type)));
};

/**
 * 从localStorage获取缓存的菜单统计数据
 * @param type 数据类型
 * @returns { data: number; lastUpdated: number } | null
 */
type MenuStatsData = { [key: string]: number };

const getCachedMenuStats = (
  type: MenuStatType = "loan"
): { data: MenuStatsData; lastUpdated: number } | null => {
  try {
    const storageKey = getStorageKey(type);
    const cached = localStorage.getItem(storageKey);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    console.error(`Failed to get cached menu stats for ${type}:`, e);
    return null;
  }
};

const getCachedMenuKey = (type: MenuStatType, menu: string): any => {
  let lowerMenu = menu.toLowerCase();
  if (type == "application") {
    switch (lowerMenu) {
      case "new":
        return "new";
      case "newReady":
        return "newReady";
      case "newready":
        return "newReady";
      case "newPendingverify":
        return "newPendingverify";
      case "newpendingverify":
        return "newPendingverify";
      case "approveClosing":
        return "approveClosing";
      case "approveclosing":
        return "approveClosing";
      case "approveNotClosing":
        return "approveNotClosing";
      case "approvenotclosing":
        return "approveNotClosing";
      case "counteredPreApprovedAccept":
        return "counteredPreApprovedAccept";
      case "counteredpreapprovedaccept":
        return "counteredPreApprovedAccept";
      case "counteredPreApprovedNotAccept":
        return "counteredPreApprovedNotAccept";
      case "counteredpreapprovednotaccept":
        return "counteredPreApprovedNotAccept";
      case "initial review":
        return "initial";
      case "hold":
        return "hold";
      case "Hold":
        return "hold";
      case "final review":
        return "final";
      case " Initial Review":
        return "initial";
      case "Final Review":
        return "final";
      case "to notify":
        return "notify";
      case "To Notify":
        return "notify";
      case "approved":
        return "approve";
      case "Approved":
        return "approve";
      case "completed":
        return "approve";
      case "Completed":
        return "approve";
      case "pre-approved":
        return "preapprove";
      case "countered":
        return "counter";
      case "Countered":
        return "counter";
      case "decline":
        return "decline";
      case "declined":
        return "decline";
      case "expired":
        return "expired";
      case "Expired":
        return "expired";
      case "incomplete":
        return "incomplete";
      case "Incomplete":
        return "incomplete";
      case "funded":
        return "funded";
      case "Funded":
        return "funded";
      default:
        return lowerMenu;
    }
  } else if (type == "loan") {
    if (lowerMenu == "Paid-Off" || lowerMenu == "paid-off") {
      return "paidOff";
    } else if (lowerMenu == "Charged-Off" || lowerMenu == "charged-off") {
      return "chargedOff";
    } else if (lowerMenu == "voided" || lowerMenu == "Voided") {
      return "voided";
    } else if (lowerMenu == "large30d") {
      return "large30D";
    } else {
      return lowerMenu;
    }
  }
  return "";
};
const saveCachedMenuStats = (
  type: MenuStatType = "loan",
  menu: string = "",
  increment: boolean = true,
  value: number = 1
): void => {
  if (menu == "Funded") {
    return;
  }
  let menukey = getCachedMenuKey(type, menu);
  console.log("menuKey is : ", menukey);
  let cached = getCachedMenuStats(type);
  if (!cached) {
    preloadMenuStats(type);
    cached = getCachedMenuStats(type);
  }

  const menudata: MenuStatsData = cached?.data || {};
  const updatedData: MenuStatsData = {
    ...menudata,
    [menukey]: increment
      ? (menudata[menukey] || 0) + value
      : (menudata[menukey] || 0) - value,
  };
  const storageKey = getStorageKey(type);
  localStorage.setItem(
    storageKey,
    JSON.stringify({
      data: updatedData,
      lastUpdated: Date.now(),
    })
  );
};
/**
 * 清除指定类型的缓存数据
 * @param type 数据类型
 */
const clearMenuStatsCache = (type: MenuStatType = "loan"): void => {
  const storageKey = getStorageKey(type);
  localStorage.removeItem(storageKey);
};

/**
 * 检查缓存是否过期
 * @param type 数据类型
 * @param maxAge 最大缓存时间(毫秒)，默认1小时
 * @returns boolean
 */
const isMenuStatsCacheExpired = (
  type: MenuStatType = "loan",
  maxAge: number = 3600000
): boolean => {
  const cached = getCachedMenuStats(type);
  return !cached || Date.now() - cached.lastUpdated > maxAge;
};

export {
  clearMenuStatsCache,
  dataProvider,
  getCachedMenuStats,
  isMenuStatsCacheExpired,
  preloadAllMenuStats,
  preloadMenuStats,
  saveCachedMenuStats,
  type MenuStatType,
};
